import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faDownload } from '@fortawesome/free-solid-svg-icons';
import WaterSummaryCard from './WaterSummaryCard';
import WaterGraphCard from './WaterGraphCard';
import WaterBalancePanel from './WaterBalancePanel';
import OvernightBaselinePanel from './OvernightBaselinePanel';
import RefreshControl, { GRAPH_QUERY_KEY } from '../RefreshControl';
import { WATER_METRICS, METRIC_GROUPS } from './waterMetrics';
import { WATER_CHARTS, INSIGHTS_TAB } from './waterCharts';
import ExecSummaryBanner from '../../../../../globalComponents/insights/ExecSummaryBanner';
import { toCsv, exportRows, exportFilename } from '../../utils/waterExport';
import { financialYearsWithData, subLocationsFor } from '../../utils/meterFilters';
import { downloadCsv } from '../../utils/downloadCsv';
import '../../styles/MetersDashboard.css';
import '../../styles/Insights.css';

/**
 * The Water Management Dashboard.
 *
 * The page it replaces held four chart components, two card components and the
 * page itself in one 1,519-line file, with the filter block, debounce and
 * range sync copied into each. Everything here is composed from one chart card,
 * one summary card and two lists of what to render, so a change to how a card
 * behaves happens once.
 *
 * The financial year, location and sub-location scope every figure below, and
 * every request resolves its window from the selected year rather than from
 * today — choosing a past year used to match nothing.
 *
 * The page carries `im-root` itself because its stylesheet is scoped under
 * that class and it is mounted from two places: the meters page, which already
 * provides it, and the Sub Admin dashboard tabs, which do not. Depending on
 * the mount point left it unstyled on the second route.
 */

/**
 * Everything on the page that reports a narrative: every chart, the water
 * balance and the overnight baseline. The summary waits for all of them before
 * it writes, so a figure left out of this count would have it write early and
 * one short.
 */
const INSIGHT_SOURCES = WATER_CHARTS.length + 2;

/**
 * react-select needs the same props on all three filters, and repeating them
 * inline hides the one difference that matters between them.
 */
const SELECT_PROPS = {
  className: 'react-select-container',
  classNamePrefix: 'react-select',
  menuPortalTarget: typeof document === 'undefined' ? null : document.body,
  menuPosition: 'fixed',
  isClearable: true,
};

/**
 * @param {Array<object>} meters - Meters, for the years they are mapped to.
 * @param {Array<object>} financialYearOptions - Years the company reports on.
 * @param {Array<object>} sourceOptions - Locations, each with its sub-locations.
 * @param {Function} onRefresh - Asks the page's owner to refetch.
 */
const WaterDashboard = ({
  meters = [],
  financialYearOptions = [],
  sourceOptions = [],
  onRefresh,
}) => {
  const queryClient = useQueryClient();
  const [financialYear, setFinancialYear] = useState();
  const [location, setLocation] = useState(null);
  const [subLocation, setSubLocation] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [narratives, setNarratives] = useState([]);

  /**
   * Only the years some meter is mapped to. A year with no mapping can draw
   * nothing but an empty dashboard, which reads as a plant that used no water.
   */
  const yearOptions = useMemo(
    () => financialYearsWithData(meters, financialYearOptions),
    [meters, financialYearOptions]
  );

  /**
   * Both filters settle once their options arrive, which is why these watch
   * props rather than running in a handler — the lists load after the first
   * render. Neither overwrites a choice already made: the year is only
   * replaced when it is no longer on offer, and the location is only filled
   * in while empty.
   */
  useEffect(() => {
    setFinancialYear((current) =>
      yearOptions.some((option) => option.value === current)
        ? current
        : yearOptions[0]?.value
    );
  }, [yearOptions]);

  /** One site is not a choice, so it is made for the reader. */
  useEffect(() => {
    if (sourceOptions.length !== 1) return;

    setLocation((current) => current ?? sourceOptions[0]);
  }, [sourceOptions]);

  const subLocationOptions = useMemo(
    () => subLocationsFor(sourceOptions, location ? [location.value] : []),
    [sourceOptions, location]
  );

  const scope = {
    currentFY: financialYear,
    locationId: location?.value,
    subLocationId: subLocation?.value,
    meters,
  };

  /**
   * Exports what the page has already fetched rather than issuing fresh
   * requests, so the file says what the reader was looking at — including
   * which window each figure was read over, since the cards and the charts
   * do not share one.
   */
  const handleExport = () => {
    const rows = exportRows(queryClient.getQueriesData(GRAPH_QUERY_KEY), {
      financialYear,
      location: location?.label,
    });

    if (rows.length < 2) return;

    downloadCsv(exportFilename(financialYear), toCsv(rows));
  };

  const handleLocationChange = (selected) => {
    setLocation(selected);
    setSubLocation(null);
  };

  /**
   * Keyed by chart rather than by title, because a chart reports again whenever
   * its own range changes and two reports for one chart would be summarised as
   * two findings.
   */
  const handleInsightsReady = useCallback((reported) => {
    setNarratives((current) => [
      ...current.filter((entry) => entry.graphId !== reported.graphId),
      reported,
    ]);
  }, []);

  /**
   * Clearing on both directions is deliberate: the summary is written from the
   * narratives it was given, so leaving yesterday's set in place would have the
   * banner reappear on the next switch-on describing charts that have since
   * been refetched.
   */
  const toggleInsights = () => {
    setShowInsights((current) => !current);
    setNarratives([]);
  };

  return (
    <div className="im-root meters-dashboard">
      <div className="dashboard-header mb-4">
        <h2>Water Management Dashboard</h2>
        <div className="header-controls">
          <div className="im-insights-toggle">
            <input
              id="wd-insights"
              type="checkbox"
              role="switch"
              checked={showInsights}
              onChange={toggleInsights}
            />
            <label htmlFor="wd-insights">
              AI insights
            </label>
          </div>

          <button
            type="button"
            className="wd-btn wd-btn--primary"
            onClick={handleExport}
            title="Download every figure on this page as a spreadsheet"
          >
            <FontAwesomeIcon icon={faDownload} />
            Export CSV
          </button>

          <RefreshControl onRefresh={onRefresh} />
        </div>
      </div>

      <div className="wd-filters">
        <h3 className="wd-filters-title">
          <FontAwesomeIcon icon={faFilter} className="me-2" />
          Global Filters
        </h3>
        <div className="wd-filters-row">
          <div className="wg-field">
            <label className="wg-label" htmlFor="wd-fy">Financial Year</label>
            <Select
              {...SELECT_PROPS}
              inputId="wd-fy"
              options={yearOptions}
              value={yearOptions.find((option) => option.value === financialYear) || null}
              onChange={(selected) => setFinancialYear(selected?.value)}
              placeholder="Select Financial Year"
            />
          </div>

          <div className="wg-field">
            <label className="wg-label" htmlFor="wd-location">Location</label>
            <Select
              {...SELECT_PROPS}
              inputId="wd-location"
              options={sourceOptions}
              value={location}
              onChange={handleLocationChange}
              placeholder="Select Location"
            />
          </div>

          {subLocationOptions.length > 0 && (
            <div className="wg-field">
              <label className="wg-label" htmlFor="wd-sublocation">Sub Location</label>
              <Select
                {...SELECT_PROPS}
                inputId="wd-sublocation"
                options={subLocationOptions}
                value={subLocation}
                onChange={setSubLocation}
                placeholder="Select Sub Location"
              />
            </div>
          )}
        </div>
      </div>

      {financialYear && (
        <WaterBalancePanel
          {...scope}
          showInsights={showInsights}
          onInsightsReady={handleInsightsReady}
        />
      )}

      {financialYear &&
        METRIC_GROUPS.map((group) => (
          <section key={group.id}>
            <h3 className="section-title">{group.title}</h3>
            <div className="ws-grid">
              {WATER_METRICS.filter((metric) => metric.group === group.id).map((metric) => (
                <WaterSummaryCard key={metric.id} metric={metric} {...scope} />
              ))}
            </div>
          </section>
        ))}

      {financialYear && (
        <OvernightBaselinePanel
          {...scope}
          showInsights={showInsights}
          onInsightsReady={handleInsightsReady}
        />
      )}

      <section className="graphs-section">
        <h3 className="section-title">Detailed Analysis</h3>

        {showInsights && (
          <ExecSummaryBanner
            resolvedInsights={narratives}
            totalGraphCount={INSIGHT_SOURCES}
            tabName={INSIGHTS_TAB}
          />
        )}

        {WATER_CHARTS.map((chart) => (
          <WaterGraphCard
            key={chart.id}
            chartId={chart.id}
            title={chart.title}
            formula={chart.formula}
            category={chart.category}
            subCategory={chart.subCategory}
            kpi={chart.kpi}
            stackByField={chart.stackByField}
            derive={chart.derive}
            graphType={chart.graphType}
            showInsights={showInsights}
            onInsightsReady={handleInsightsReady}
            {...scope}
          />
        ))}
      </section>
    </div>
  );
};

export default WaterDashboard;
